import type { ReactNode } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldRow } from '@/features/store-settings';

import type { ChatbotCommandDescriptor } from '../../model/types';

export interface CommandsFieldProps {
  label: ReactNode;
  description?: ReactNode;
  ariaLabel?: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  commands: ChatbotCommandDescriptor[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** The scope's effective `handoffEnabled` — a `handOff` command shows the warning caption instead
   * of being disabled while it is off (design §17.3.1, closing the shipped defect that locked
   * `/operator` and prevented unticking it). */
  handoffEnabled: boolean;
}

/**
 * Dictionary-fed commands editor (design §17.3.1) — replaces the static `CHATBOT_KNOWN_COMMANDS`
 * `MultiToggleField`. `alwaysOn` commands render as a locked, always-checked row; the rest as a
 * two-column checkbox grid with per-flag hints; an unknown stored code is a removable warning row.
 */
export function CommandsField({
  label,
  description,
  ariaLabel,
  value,
  onChange,
  disabled = false,
  commands,
  isLoading,
  isError,
  onRetry,
  handoffEnabled,
}: CommandsFieldProps) {
  const { t } = useTranslation('chatbot');

  if (isLoading) {
    return (
      <FieldRow label={label} description={description} alignTop>
        <Stack spacing={1}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="70%" />
        </Stack>
      </FieldRow>
    );
  }

  if (isError || !commands) {
    return (
      <FieldRow label={label} description={description} alignTop>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {value.map((code) => (
              <Chip
                key={code}
                size="small"
                variant="outlined"
                label={`/${code}`}
                sx={{ fontFamily: 'monospace' }}
              />
            ))}
          </Stack>
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={onRetry}>
                {t('dictionaries.retry')}
              </Button>
            }
          >
            {t('dictionaries.loadError')}
          </Alert>
        </Stack>
      </FieldRow>
    );
  }

  const alwaysOn = commands.filter((c) => c.alwaysOn);
  const selectable = commands.filter((c) => !c.alwaysOn);
  const alwaysOnCodes = alwaysOn.map((c) => c.code);
  const knownCodes = new Set(commands.map((c) => c.code));
  const unknownCodes = value.filter((code) => !knownCodes.has(code));

  const toggle = (code: string, checked: boolean) => {
    const nextSelected = checked
      ? [...selectable.filter((c) => value.includes(c.code) || c.code === code).map((c) => c.code)]
      : value.filter(
          (v) => v !== code && !alwaysOnCodes.includes(v) && !unknownCodes.includes(v),
        );
    onChange([...alwaysOnCodes, ...nextSelected, ...unknownCodes]);
  };

  const removeUnknown = (code: string) => {
    onChange(value.filter((v) => v !== code));
  };

  return (
    <FieldRow label={label} description={description} alignTop>
      <FormGroup aria-label={ariaLabel}>
        {alwaysOn.map((command) => (
          <FormControlLabel
            key={command.code}
            control={
              <Checkbox
                size="small"
                checked
                disabled
                icon={<LockOutlinedIcon fontSize="small" />}
                checkedIcon={<LockOutlinedIcon fontSize="small" />}
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{t(`behaviour.commands.items.${command.code}`, `/${command.code}`)}</span>
                <Tooltip title={t('behaviour.commands.alwaysOnTooltip')}>
                  <Chip size="small" variant="outlined" label={t('behaviour.commands.alwaysOn')} />
                </Tooltip>
              </Stack>
            }
          />
        ))}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 2,
          }}
        >
          {selectable.map((command) => {
            const checked = value.includes(command.code);
            const showHandOffWarning = command.handOff && !handoffEnabled;
            return (
              <Box key={command.code}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled}
                      onChange={(event) => toggle(command.code, event.target.checked)}
                    />
                  }
                  label={t(`behaviour.commands.items.${command.code}`, `/${command.code}`)}
                />
                {command.parameterised ? (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ pl: 4 }}>
                    {t('behaviour.commands.flags.parameterised')}
                  </Typography>
                ) : null}
                {command.requiresLinkedCustomer ? (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ pl: 4 }}>
                    {t('behaviour.commands.flags.requiresLinkedCustomer')}
                  </Typography>
                ) : null}
                {command.handOff ? (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ pl: 4 }}>
                    {t('behaviour.commands.flags.handOff')}
                  </Typography>
                ) : null}
                {showHandOffWarning ? (
                  <Typography variant="caption" color="warning.main" component="p" sx={{ pl: 4 }}>
                    {t('behaviour.commands.flags.handOffOff')}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
        {unknownCodes.map((code) => (
          <FormControlLabel
            key={code}
            control={
              <Checkbox
                size="small"
                checked
                disabled={disabled}
                onChange={() => removeUnknown(code)}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {t('behaviour.commands.unknownCommand', { command: `/${code}` })}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </FieldRow>
  );
}
